import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { useCallback, useState } from 'react';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import type { Catalog } from '@data/fms/vehicle/types';
import { useAtomCallback } from 'jotai/utils';

/**
 * カタログ一覧取得API
 */
export const useCatalogsAPI = (): {
  state: APIState;
  getCatalogs: () => Promise<Catalog[]>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getCatalogsAPI = useCallback(
    async (projectId: string): Promise<Result<Catalog[], AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(`/${projectId}/vehicle_catalogs`);
        if (!isNullOrUndefined(res?.data) && Array.isArray(res?.data)) {
          return new Success(res?.data);
        }
        return new Success([]);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getCatalogs = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        if (isNullOrUndefined(project)) {
          notifyError({
            message: t('api.fms.get_catalogs', { status: 'failed' }),
          });
          setState('hasError');
          return [];
        }
        setState('loading');
        const res = await getCatalogsAPI(project!.id);
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_catalogs', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return [];
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, getCatalogsAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    getCatalogs,
  };
};
